import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BenchlingQueryResult, QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PROCEDURE_WITH_PRECONFIGURED_ANALYSIS_TEMPLATES_FRAGMENT} from 'procedures/shared-components/analysis/PreconfiguredAnalysisMissingTemplatesWarningBanner';
import {ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE} from 'studies/StudyConstants';
import LastRefetchedHeader from 'studies/typed/shared/LastRefetchedHeader';
import {StudyOverviewViewQuery} from 'studies/workspace/overview/__generated/StudyOverviewViewQuery';
import useOnChangeOnly from 'ui-library/hooks/useOnChangeOnly';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StudyQueryErrorBanner, StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  ClinicalBasedStudyExecutionOverviewQuery,
  ClinicalBasedStudyExecutionOverviewQueryVariables,
} from './__generated/ClinicalBasedStudyExecutionOverviewQuery';
import {ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_sampleSummary_SampleSummaryNode as SampleSummaryNode} from './__generated/ClinicalBasedStudyExecutionOverviewQuery';
import AssayRunsTable, {AssayRunEmptyState} from './AssayRunsTable';
import SampleTable from './SampleTable';
import {
  getSampleDefinitionSummaries,
  getSampleTestPlanProcedures,
  getUncategorizedSampleDefinitionSummary,
  SampleDefinitionReceivingSummary,
  SampleDefinitionSummary,
} from './SampleTableUtils';

type StudyNode = ExtractByTypename<ClinicalBasedStudyExecutionOverviewQuery['study'], 'StudyNode'>;
type GeneralStudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;

export const CLINICAL_BASED_STUDY_EXECUTION_OVERVIEW_QUERY: DocumentNode = gql`
  # study.activeProcedureRuns.conditions does not query id
  # eslint-disable benchlint/id-field-required
  query ClinicalBasedStudyExecutionOverviewQuery($studyId: StrictString!, $numActiveProcedureRuns: StrictInt!)
  @skipAudit
  @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        phase
        studySchema {
          ... on StudySchemaNode {
            id
            studyType
          }
        }
        studyDesign {
          ... on TrialSampleTestingStudyDesignNode {
            id
            sampleTestPlans {
              ... on StudySampleTestPlanNode {
                id
                sampleDefinition {
                  id
                }
                procedure {
                  ... on ProcedureNode {
                    ...ProcedureWithPreconfiguredAnalysisTemplatesFragment
                    archiveData {
                      archived
                    }
                    procedureFlowchart {
                      ... on ProcedureFlowchartNode {
                        id
                        publishedFlowchartVersion {
                          ... on ProcedureFlowchartVersionNode {
                            id
                          }
                        }
                      }
                    }
                    publishedProcedureFlowchartConfigVersionProxy {
                      ... on ProcedureFlowchartConfigVersionProxyNode {
                        id
                      }
                    }
                    currentPublishingRecord {
                      ... on PublishingRecordNode {
                        id
                        publishingStatus
                      }
                      ...InaccessibleInfoGraphQL
                    }
                  }
                  ...InaccessibleInfoGraphQL
                }
                isReadyForExecution
              }
            }
          }
        }
        activeProcedureRuns(limit: $numActiveProcedureRuns) {
          ... on ProcedureRunNode {
            id
            name
            createdAt
            creator {
              ... on CreatorInterface {
                id
                name
                handle
                avatarUrl
              }
            }
            conditions {
              name
            }
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                procedureFlowchart {
                  ... on ProcedureFlowchartNode {
                    id
                    parent {
                      id
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            procedureFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
                procedure {
                  id
                }
              }
              ...InaccessibleInfoGraphQL
            }
            methodExecutionInstances {
              id
              templateMethodVersion {
                ... on ProcedureMethodDefinitionVersionNode {
                  id
                }
                ...InaccessibleInfoGraphQL
              }
              worksheet {
                ... on WorksheetNode {
                  id
                  name
                  parentReviewLifecycles {
                    ... on ReviewLifecycleNode {
                      id
                    }
                  }
                  review {
                    ... on ReviewNode {
                      id
                      status
                    }
                  }
                  tasks {
                    ... on ProcessTaskNode {
                      id
                      status {
                        ... on ProcessTaskStatusNode {
                          id
                          statusType
                          displayName
                        }
                      }
                    }
                    ...InaccessibleInfoGraphQL
                  }
                }
                ...InaccessibleInfoGraphQL
              }
              methodTask {
                ... on ProcessTaskNode {
                  id
                  assignee {
                    ... on UserNode {
                      id
                      name
                      handle
                      avatarUrl
                    }
                  }
                  status {
                    ... on ProcessTaskStatusNode {
                      id
                      statusType
                    }
                  }
                }
                ...InaccessibleInfoGraphQL
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        sampleSummary {
          ... on SampleSummaryNode {
            sampleDefinitionSummaries {
              ... on SampleDefinitionSummaryNode {
                receivedCount
                discrepancyCount
                testOrderSummaries {
                  ... on TestOrderSummaryNode {
                    procedure {
                      ... on ProcedureNode {
                        id
                      }
                      ...InaccessibleInfoGraphQL
                    }
                    resolvedCount
                    totalCount
                  }
                }
                sampleDefinition {
                  ... on SampleDefinitionNode {
                    id
                    label
                    treatmentGroup {
                      ... on TreatmentGroupNode {
                        id
                        targetNumberOfSubjects
                      }
                    }
                  }
                }
              }
            }
            uncategorizedSampleDefinitionSummary {
              ... on UncategorizedSampleDefinitionSummaryNode {
                receivedCount
                discrepancyCount
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${PROCEDURE_WITH_PRECONFIGURED_ANALYSIS_TEMPLATES_FRAGMENT}
`;

export default function ClinicalStudyExecutionOverview({study}: {study: GeneralStudyNode}): JSX.Element {
  const result = useBenchlingQuery<
    ClinicalBasedStudyExecutionOverviewQuery,
    ClinicalBasedStudyExecutionOverviewQueryVariables
  >(CLINICAL_BASED_STUDY_EXECUTION_OVERVIEW_QUERY, {
    variables: {studyId: study.id, numActiveProcedureRuns: ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE + 1},
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return (
    <div>
      <div className={css(styles.refreshHeaderContainer)}>
        <LastRefetchedHeader result={result} />
      </div>
      <ClinicalStudyExecutionOverviewTablesContainer result={result} />
    </div>
  );
}

function ClinicalStudyExecutionOverviewTablesContainer({
  result,
}: {
  result: BenchlingQueryResult<ClinicalBasedStudyExecutionOverviewQuery>;
}): JSX.Element {
  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (result.type === QueryResultType.ERROR) {
    return <StudyQueryErrorBanner />;
  } else {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study}
          modelType={ModelTypeConstants.STUDY}
          displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
        />
      );
    }
    const activeProcedureRuns = data.study.activeProcedureRuns.filter(
      (activeProcedureRun) => !isInaccessible(activeProcedureRun)
    );
    if (activeProcedureRuns.length === 0 && isInaccessible(data.study.activeProcedureRuns[0])) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study.activeProcedureRuns[0]}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText="The assay run(s) of this study are not accessible. The assay run(s) associated with this study may have been moved into a project that you do not have access to."
        />
      );
    }
    return (
      <RenderClinicalStudyExecutionOverviewTables
        study={data.study}
        sampleSummary={data.study.sampleSummary}
        refetch={result.refetch}
      />
    );
  }
}

function RenderClinicalStudyExecutionOverviewTables({
  study,
  sampleSummary,
  refetch,
}: {
  study: StudyNode;
  sampleSummary: SampleSummaryNode | null;
  refetch: () => void;
}): JSX.Element {
  // Refetch only when phase actually changes; avoid loops caused by loading branch unmount/remount.
  // refetch is required to update assay run empty state and sample table
  useOnChangeOnly(() => {
    refetch();
  }, [study.phase]);
  const getAssayRunEmptyState = (
    sampleDefinitionSummaries: Array<SampleDefinitionSummary>,
    uncategorizedSampleDefinitionSummary: SampleDefinitionReceivingSummary | null
  ): AssayRunEmptyState => {
    if (sampleDefinitionSummaries.length > 0) {
      return AssayRunEmptyState.HAS_SAMPLE_DEFINITIONS;
    } else if (sampleDefinitionSummaries.length === 0 && !uncategorizedSampleDefinitionSummary) {
      return AssayRunEmptyState.HAS_NO_SAMPLES;
    } else {
      return AssayRunEmptyState.HAS_UNCATEGORIZED_SAMPLES;
    }
  };

  const sampleDefinitionSummaries = getSampleDefinitionSummaries(sampleSummary);
  const uncategorizedSampleDefinitionSummary = getUncategorizedSampleDefinitionSummary(sampleSummary);
  const assayRunEmptyState = getAssayRunEmptyState(
    sampleDefinitionSummaries,
    uncategorizedSampleDefinitionSummary
  );
  const sampleTestPlanProcedures = getSampleTestPlanProcedures(study.studyDesign?.sampleTestPlans ?? []);
  return (
    <div>
      <SampleTable
        studyId={study.id}
        studyType={study.studySchema.studyType}
        sampleDefinitionSummaries={sampleDefinitionSummaries}
        uncategorizedSampleDefinitionSummary={uncategorizedSampleDefinitionSummary}
        sampleTestPlanProcedures={sampleTestPlanProcedures}
      />
      <AssayRunsTable study={study} assayRunEmptyState={assayRunEmptyState} />
    </div>
  );
}

const styles = StyleSheet.create({
  refreshHeaderContainer: {
    marginTop: 16,
    marginBottom: 8,
  },
  loadingDiv: {
    paddingBottom: 5,
  },
});
